import React, { useCallback, useEffect, useMemo, useState } from "react";

import Popover from "antd/es/popover";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { StageSummary } from "@mapmycustomers/shared/types/entity/deals/StageSummary";
import { LoadingSpinner } from "@mapmycustomers/ui";

import EntityBadge from "@app/component/EntityBadge";
import useView from "@app/component/view/utils/useView";
import localSettings from "@app/config/LocalSettings";
import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import loggingService from "@app/util/logging";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import DataItem from "./DataItem";
import styles from "./FunnelChart.module.scss";

interface Props {
  loading: boolean;
  stagesSummary: StageSummary[];
}

const messages = defineMessages({
  adjValue: {
    id: "deals.funnelChart.tooltip.adjValue",
    defaultMessage: "Adj. Value",
    description: "Label for displaying adj value",
  },
  allTimeDealsCount: {
    id: "deals.funnelChart.tooltip.allTimeDealsCount",
    defaultMessage: "All-time # of deals",
    description: "Label for displaying all time deals count",
  },
  currentDealsCount: {
    id: "deals.funnelChart.tooltip.currentDealsCount",
    defaultMessage: "Current # of deals",
    description: "Label for displaying current deals count",
  },
  percentDeals: {
    id: "deals.funnelChart.tooltip.percentDeals",
    defaultMessage: "% deals made stage",
    description: "Label for displaying percent of deals made stage",
  },
  totalValue: {
    id: "deals.funnelChart.tooltip.totalValue",
    defaultMessage: "Total value",
    description: "Label for displaying total value",
  },
  weight: {
    id: "deals.funnelChart.tooltip.weight",
    defaultMessage: "Weight",
    description: "Label for displaying weight",
  },
});

const BASE_TRAP_HEIGHT = 152;
const MIN_TRAP_WIDTH = 60;

const FunnelChart: React.FC<Props> = ({ loading, stagesSummary }) => {
  const intl = useIntl();
  const {
    updateFilterModel,
    viewState: { filter },
  } = useView<Deal>();
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
  const allDealsCountsByStage = useMemo(
    () =>
      stagesSummary.map(
        (el, index, arr) =>
          el.totalDeals + arr.slice(index + 1).reduce((s, v) => s + v.totalDeals, 0)
      ),
    [stagesSummary]
  );
  const totalCount = allDealsCountsByStage[0];
  const selectedStageIds: Array<Stage["id"]> = useMemo(() => {
    if (!filter.stage || !isSimpleCondition(filter.stage)) {
      return [];
    }

    return filter.stage.value;
  }, [filter]);
  const handleSelectedStage = useCallback(
    (stageId: Stage["id"], isSelected: boolean) => () => {
      const newStageIds = isSelected
        ? selectedStageIds.filter((id) => id !== stageId)
        : selectedStageIds.concat(stageId);
      updateFilterModel({
        stage: newStageIds.length
          ? { operator: FilterOperator.IN_ANY, value: newStageIds }
          : undefined,
      });
    },
    [updateFilterModel, selectedStageIds]
  );

  useEffect(() => {
    try {
      const observer = new ResizeObserver(() => {
        setContainerWidth(containerElement?.offsetWidth || 0);
      });

      if (containerElement) {
        observer.observe(containerElement);
      }

      return () => {
        observer.disconnect();
      };
    } catch (e) {
      loggingService.error("Error in FunnelChart", e);
    }
  }, [containerElement, setContainerWidth]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.container} ref={setContainerElement}>
      <div className={styles.scrollContainer}>
        {allDealsCountsByStage.map((count, index, arr) => {
          const right = arr[index + 1] || 0;
          const borderSize = (BASE_TRAP_HEIGHT * (count - right)) / (2 * totalCount);
          const shift = (BASE_TRAP_HEIGHT * (totalCount - count)) / (2 * totalCount);
          const widthCalculated = Math.ceil(containerWidth / arr.length) - 2;
          const width = widthCalculated >= MIN_TRAP_WIDTH ? widthCalculated : MIN_TRAP_WIDTH;
          const heightCalculated = Math.ceil((BASE_TRAP_HEIGHT * count) / totalCount);
          const percent = ((count / totalCount) * 100).toFixed(2);
          const currentStageSummary = stagesSummary[index];
          const isStageSelected = selectedStageIds.includes(currentStageSummary.stage.id);
          const isAnySelected = selectedStageIds.length !== 0;
          const name = currentStageSummary.stage.name;
          const colorHex = anyColorToHex(currentStageSummary.stage.color);
          const isAnySelectedButNotThis =
            isAnySelected && !selectedStageIds.includes(currentStageSummary.stage.id);
          const colorFinal = !isAnySelected || isStageSelected ? colorHex : colors.primary010;

          return (
            <div
              className={styles.stage}
              key={index}
              onClick={handleSelectedStage(currentStageSummary.stage.id, isStageSelected)}
              style={{ width: `${width}px` }}
            >
              <div className={styles.boxContainer}>
                <Popover
                  content={
                    <div className={styles.popoverContent}>
                      <EntityBadge entity={currentStageSummary.stage} />
                      <DataItem
                        label={intl.formatMessage(messages.currentDealsCount)}
                        value={currentStageSummary.totalDeals}
                      />
                      <DataItem
                        label={intl.formatMessage(messages.allTimeDealsCount)}
                        value={count}
                      />
                      <DataItem
                        label={intl.formatMessage(messages.percentDeals)}
                        value={`${percent}%`}
                      />
                      <DataItem
                        label={intl.formatMessage(messages.totalValue)}
                        value={intl.formatNumber(currentStageSummary.totalValue, {
                          currency: localSettings.getCurrencyCode(),
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 0,
                          style: "currency",
                        })}
                      />
                      <DataItem
                        label={intl.formatMessage(messages.weight)}
                        value={`${currentStageSummary.stage.weight * 100}%`}
                      />
                      <DataItem
                        label={intl.formatMessage(messages.adjValue)}
                        value={intl.formatNumber(
                          currentStageSummary.totalValue * currentStageSummary.stage.weight,
                          {
                            currency: localSettings.getCurrencyCode(),
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 0,
                            style: "currency",
                          }
                        )}
                      />
                    </div>
                  }
                  placement="bottom"
                >
                  <div
                    className={styles.trap}
                    style={{
                      borderBottomWidth: `${borderSize}px`,
                      borderLeftColor: colorFinal,
                      borderLeftWidth: `${width}px`,
                      borderTopWidth: `${borderSize}px`,
                      height: `${heightCalculated}px`,
                      marginTop: `${shift}px`,
                    }}
                  />
                </Popover>
              </div>
              <div className={styles.label}>
                <div className={cn(styles.stageName, isAnySelectedButNotThis && styles.selected)}>
                  {name}
                </div>
                <div
                  className={cn(styles.stagePercent, isAnySelectedButNotThis && styles.selected)}
                >
                  {percent}%
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FunnelChart;
